import { authHeaders } from "./save";

/**
 * EARLY-ACCESS CODES, the client half (server/accessCodes.js has the rules and the locks). A code typed on the
 * pre-launch page either binds to the signed-in account at once (`granted`) or gives this browser a PASS that opens the
 * site until the visitor signs in, when `claimAccess` binds it to their new account. The pass lives in an HttpOnly
 * cookie the server sets — page script never sees it, which is why these calls carry no token of their own.
 */
export interface AccessStatus {
  prelaunch: boolean;
  /** this account may play (an admin, or it holds a bound code) */
  granted: boolean;
  /** this browser holds a pass for a code not yet bound — sign in to claim it */
  pass: boolean;
  signedIn: boolean;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { ...init, credentials: "same-origin", headers: await authHeaders({ "content-type": "application/json", ...(init.headers as Record<string, string> ?? {}) }) });
  const body = await res.json().catch(() => ({})) as T & { error?: string };
  if (!res.ok) throw new Error(body.error || `The request failed (${res.status}). Try again in a moment.`);
  return body;
}

export function accessStatus(): Promise<AccessStatus> {
  return call<AccessStatus>("/api/access/status");
}

/** Enter a code: `granted` (bound to this account) or `pass` (the site is open — create an account next). */
export function redeemAccessCode(code: string): Promise<{ ok: true; granted?: boolean; pass?: boolean }> {
  return call("/api/access/redeem", { method: "POST", body: JSON.stringify({ code }) });
}

/** Signed in with a pass: bind its code to this account. */
export function claimAccess(): Promise<{ ok: true; granted: true }> {
  return call("/api/access/claim", { method: "POST", body: "{}" });
}
